import * as api from '../../requests'
import * as WizardClasses from './WizardClasses'
import { loopThroughRules } from './validate'
import store, { resolveFieldLogic, resolveFieldEngine } from '../../store'
import deepEqual from 'deep-equal'
import {
  getFieldsWithChangedValues,
  getFieldsByType,
  objectToArray,
  getRequiredAppSettingNames,
} from 'utilities'
import { resolveFieldConditions } from '@tecuity/wizard-utilities'
import { getAppSettingsByName } from 'components/AppSettings/AppSettingActions'
import x2js from 'x2js'
import { extendUserSession } from '../../Auth/AuthActions'
import { setErrorToast } from '../../actions'
import { SET_WIZARD_SECONDARY_VALIDATION } from './WizardReducer'
const CONDITIONALLY_SHOW_SIGNATURES_FOR_CORRECTIONS =
  process.env.CONDITIONALLY_SHOW_SIGNATURES_FOR_CORRECTIONS
const xml2Json = new x2js()
const { CreateInstancesFromJson } = WizardClasses

export const populateWizard = (wizard) => ({
  type: 'POPULATE_WIZARD',
  ...wizard,
})

export const toggleWizardLoading = (loading) => ({
  type: 'TOGGLE_WIZARD_LOADING',
  loading,
})

export const setWizardErrorMessage = (message, title) => ({
  type: 'SET_WIZARD_ERROR_MESSAGE',
  message,
  title,
})

export const fetchAndPopulateWizard = (formDefinitionId, url, callback) => (dispatch, getState) => {
  dispatch(toggleWizardLoading(true))
  api.LOAD_WIZARD_INJECT_METADATA(
    formDefinitionId,
    null,
    false,
    (data) => {
      const preloadedData = data.FIELD_VALUES || {}
      let parsedWizard = CreateInstancesFromJson(data.STEPS_JSON, preloadedData)
      const meta = xml2Json.xml2js(data.METADATA) ? xml2Json.xml2js(data.METADATA).METADATA : {}
      prefetchRequiredData(parsedWizard.fields)
        .then((fields) => preFetchMergeCodes(fields, meta))
        .then((fields) => {
          const publicWizard =
            url.includes('/forms/') ||
            url.includes('/amend/') ||
            url.includes('/draft/') ||
            url.includes('/view/') ||
            url.includes('/correct/')
          const staffWizard = url.includes('/office/')

          const filteredStepOrder = publicWizard
            ? parsedWizard.stepOrder.filter((key) =>
                parsedWizard.steps[key].visibleTo.includes('Public'),
              )
            : staffWizard
              ? parsedWizard.stepOrder.filter((key) =>
                  parsedWizard.steps[key].visibleTo.includes('SOSStaff'),
                )
              : parsedWizard.stepOrder

          let filteredSteps = publicWizard
            ? Object.fromEntries(
                Object.entries(parsedWizard.steps).filter(([key, val]) =>
                  val.visibleTo.includes('Public'),
                ),
              )
            : staffWizard
              ? Object.fromEntries(
                  Object.entries(parsedWizard.steps).filter(([key, val]) =>
                    val.visibleTo.includes('SOSStaff'),
                  ),
                )
              : parsedWizard.steps

          const filteredSections = publicWizard
            ? Object.fromEntries(
                Object.entries(parsedWizard.sections).filter(([key, val]) =>
                  val.visibleTo.includes('Public'),
                ),
              )
            : staffWizard
              ? Object.fromEntries(
                  Object.entries(parsedWizard.sections).filter(([key, val]) =>
                    val.visibleTo.includes('SOSStaff'),
                  ),
                )
              : parsedWizard.sections

          Object.entries(filteredSteps).forEach(
            (step) =>
              (step[1].sectionOrder = step[1].sectionOrder.filter((section) =>
                Object.keys(filteredSections).includes(section),
              )),
          )
          dispatch(
            populateWizard({
              formDefinitionId,
              title: data.TITLE,
              stepOrder: filteredStepOrder,
              steps: filteredSteps,
              sections: filteredSections,
              fields,
              description: data.DESCR,
              sourceId: data.SOURCE_ID,
              meta,
              draftId: false,
              mode: data.MODE,
              category: data.CATEGORY,
              docTitleSrc: data.DOC_TITLE_SRC,
              docId: data.DOC_ID,
              formTitle: data.FORM_TITLE,
              requireStaffEntry: data.REQUIRE_STAFF_ENTRY_YN,
              showSignatures: data.SHOW_SIGNATURES,
              internalReviewRequired: data.INTERNAL_REVIEW_REQUIRED_YN,
              requirePrintMailMethod: data.PRINT_MAIL_REQUIRED_YN,
              onlyRenderChangedFieldsOnForm: data.ONLY_SHOW_CHANGED_FIELDS_ON_FORM_YN,
              noLoginRequired: data.PUBLIC_YN,
              feeDescription: data.FEE_DESCR,
              draftsDisabled: data.DRAFTS_DISABLED,
              backOfficeOnly: data.BACK_OFFICE_ONLY_YN,
              thirdPartySigningAllowed: data.AUTHORIZED_THIRD_PARTY_YN,
              globalVariables: data.GLOBAL_VARIABLES ? data.GLOBAL_VARIABLES : undefined,
              wizardAttributesLogic: data.WIZARD_ATTRIBUTES_LOGIC
                ? data.WIZARD_ATTRIBUTES_LOGIC
                : undefined,
            }),
          )
          if (callback) {
            callback()
          }
        })
    },
    (err) => {
      console.error(err)
      dispatch(setWizardErrorMessage(err.message || null, err.title))
      dispatch(setWizardLoadFailed(true))
      dispatch(toggleWizardLoading(false))
    },
    '',
    url.includes('miscellaneous')
  )
}

export const fetchAndPopulateSecondaryReview = (formDefinitionId, filingNum) => {
  return (dispatch, getState) => {
    dispatch({ type: SET_WIZARD_SECONDARY_VALIDATION, secondaryValidation: true })
    dispatch(toggleWizardLoading(true))
    // api call to load wizard
    api.LOAD_SECONDARY_VALIDATION_WIZARD(
      formDefinitionId,
      filingNum,
      (wizard) => {
        const steps = wizard.STEPS_JSON
        const fieldValues = wizard.SecondaryValidationFieldValues
        const parsedWizard = CreateInstancesFromJson(steps, fieldValues, {
          forceAllowPrepopulation: true,
        })
        const meta = xml2Json.xml2js(wizard.METADATA)
          ? xml2Json.xml2js(wizard.METADATA).METADATA
          : {}

        prefetchRequiredData(parsedWizard.fields)
          .then((fields) => preFetchMergeCodes(fields, meta))
          .then((fields) => {
            const publicWizard = false
            const staffWizard = true

            const original = getOriginalValues(fields)
            const originalByFieldId = getOriginalByFieldId(fields)

            const filteredStepOrder = publicWizard
              ? parsedWizard.stepOrder.filter((key) =>
                  parsedWizard.steps[key].visibleTo.includes('Public'),
                )
              : staffWizard
                ? parsedWizard.stepOrder.filter((key) =>
                    parsedWizard.steps[key].visibleTo.includes('SOSStaff'),
                  )
                : parsedWizard.stepOrder

            let filteredSteps = publicWizard
              ? Object.fromEntries(
                  Object.entries(parsedWizard.steps).filter(([key, val]) =>
                    val.visibleTo.includes('Public'),
                  ),
                )
              : staffWizard
                ? Object.fromEntries(
                    Object.entries(parsedWizard.steps).filter(([key, val]) =>
                      val.visibleTo.includes('SOSStaff'),
                    ),
                  )
                : parsedWizard.steps

            const filteredSections = publicWizard
              ? Object.fromEntries(
                  Object.entries(parsedWizard.sections).filter(([key, val]) =>
                    val.visibleTo.includes('Public'),
                  ),
                )
              : staffWizard
                ? Object.fromEntries(
                    Object.entries(parsedWizard.sections).filter(([key, val]) =>
                      val.visibleTo.includes('SOSStaff'),
                    ),
                  )
                : parsedWizard.sections

            Object.entries(filteredSteps).forEach(
              (step) =>
                (step[1].sectionOrder = step[1].sectionOrder.filter((section) =>
                  Object.keys(filteredSections).includes(section),
                )),
            )
            dispatch(
              populateWizard({
                title: wizard.TITLE,
                stepOrder: filteredStepOrder,
                steps: filteredSteps,
                sections: filteredSections,
                fields,
                description: wizard.DESCR,
                sourceId: wizard.SOURCE_ID,
                meta,
                docTitleSrc: wizard.DOC_TITLE_SRC,
                draftId: false,
                amendmentNum: filingNum,
                docId: parsedWizard.docId,
                original,
                originalByFieldId,
                formTitle: wizard.FormTitle,
                requireStaffEntry: wizard.REQUIRE_STAFF_ENTRY_YN,
                showSignatures: wizard.SHOW_SIGNATURES,
                internalReviewRequired: wizard.INTERNAL_REVIEW_REQUIRED_YN,
                requirePrintMailMethod: wizard.PRINT_MAIL_REQUIRED_YN,
                onlyRenderChangedFieldsOnForm: wizard.ONLY_SHOW_CHANGED_FIELDS_ON_FORM_YN,
                formDefinitionId: wizard.FormDefinitionId,
                category: wizard.CATEGORY,
                noLoginRequired: wizard.PUBLIC_YN,
                feeDescription: wizard.FEE_DESCR,
                draftsDisabled: wizard.DRAFTS_DISABLED,
                backOfficeOnly: wizard.BACK_OFFICE_ONLY_YN,
                thirdPartySigningAllowed: wizard.AUTHORIZED_THIRD_PARTY_YN,
                globalVariables: wizard.GLOBAL_VARIABLES ? wizard.GLOBAL_VARIABLES : undefined,
                wizardAttributesLogic: wizard.WIZARD_ATTRIBUTES_LOGIC
                  ? wizard.WIZARD_ATTRIBUTES_LOGIC
                  : undefined,
                mode: wizard.MODE,
              }),
            )
          })
      },
      (err) => {
        console.error(err)
        dispatch(setWizardErrorMessage(err.message || null, err.title))
        dispatch(setWizardLoadFailed(true))
        dispatch(toggleWizardLoading(false))
      },
    )
  }
}

export const fetchAndPopulateStaffDraft = (ImageGroupID, url) => (dispatch) => {
  dispatch(toggleWizardLoading(true))
  api.LOAD_STAFF_WIZARD_DRAFT_BY_ID(
    ImageGroupID,
    (data) => {
      const steps = data.FormDefinition.STEPS_JSON
      const draft = JSON.parse(data.WizardJson)
      const wizardState = JSON.parse(data.WizardState)
      const parsedWizard = CreateInstancesFromJson(steps, draft.FIELD_VALUES, {
        forceAllowPrepopulation: true,
      })
      const meta = wizardState.wizard && wizardState.wizard.meta ? wizardState.wizard.meta : {}
      prefetchRequiredData(parsedWizard.fields)
        .then((fields) => preFetchMergeCodes(fields, meta))
        .then((fields) => {
          const publicWizard =
            url.includes('/forms/') ||
            url.includes('/amend/') ||
            (url.includes('/draft/') && !url.includes('/office/')) ||
            url.includes('/view/') ||
            url.includes('/correct/')
          const staffWizard = url.includes('/office/')

          const filteredStepOrder = publicWizard
            ? parsedWizard.stepOrder.filter((key) =>
                parsedWizard.steps[key].visibleTo.includes('Public'),
              )
            : staffWizard
              ? parsedWizard.stepOrder.filter((key) =>
                  parsedWizard.steps[key].visibleTo.includes('SOSStaff'),
                )
              : parsedWizard.stepOrder

          let filteredSteps = publicWizard
            ? Object.fromEntries(
                Object.entries(parsedWizard.steps).filter(([key, val]) =>
                  val.visibleTo.includes('Public'),
                ),
              )
            : staffWizard
              ? Object.fromEntries(
                  Object.entries(parsedWizard.steps).filter(([key, val]) =>
                    val.visibleTo.includes('SOSStaff'),
                  ),
                )
              : parsedWizard.steps

          const filteredSections = publicWizard
            ? Object.fromEntries(
                Object.entries(parsedWizard.sections).filter(([key, val]) =>
                  val.visibleTo.includes('Public'),
                ),
              )
            : staffWizard
              ? Object.fromEntries(
                  Object.entries(parsedWizard.sections).filter(([key, val]) =>
                    val.visibleTo.includes('SOSStaff'),
                  ),
                )
              : parsedWizard.sections

          Object.entries(filteredSteps).forEach(
            (step) =>
              (step[1].sectionOrder = step[1].sectionOrder.filter((section) =>
                Object.keys(filteredSections).includes(section),
              )),
          )
          dispatch(
            populateWizard({
              ...wizardState,
              title: data.FormDefinition.TITLE,
              stepOrder: filteredStepOrder,
              steps: filteredSteps,
              sections: filteredSections,
              fields,
              description: data.FormDefinition.DESCR,
              sourceId: data.FormDefinition.SOURCE_ID,
              meta,
              docTitleSrc: data.FormDefinition.DOC_TITLE_SRC,
              draftId: false,
              amendmentNum: wizardState.wizard.amendmentNum,
              docId: parsedWizard.docId,
              original: wizardState.diff.original,
              originalByFieldId: wizardState.diff.originalByFieldId,
              formTitle: data.FormTitle,
              requireStaffEntry: data.REQUIRE_STAFF_ENTRY_YN,
              showSignatures: data.SHOW_SIGNATURES,
              internalReviewRequired: data.FormDefinition.INTERNAL_REVIEW_REQUIRED_YN,
              requirePrintMailMethod: data.PRINT_MAIL_REQUIRED_YN,
              onlyRenderChangedFieldsOnForm:
                data.FormDefinition.ONLY_SHOW_CHANGED_FIELDS_ON_FORM_YN,
              formDefinitionId: data.FormDefinitionId,
              category: data.FormDefinition.CATEGORY,
              noLoginRequired: data.FormDefinition.PUBLIC_YN,
              feeDescription: data.FormDefinition.FEE_DESCR,
              draftsDisabled: data.FormDefinition.DRAFTS_DISABLED,
              backOfficeOnly: data.FormDefinition.BACK_OFFICE_ONLY_YN,
              thirdPartySigningAllowed: data.FormDefinition.AUTHORIZED_THIRD_PARTY_YN,
              globalVariables: data.FormDefinition.GLOBAL_VARIABLES
                ? data.FormDefinition.GLOBAL_VARIABLES
                : undefined,
              wizardAttributesLogic: data.FormDefinition.WIZARD_ATTRIBUTES_LOGIC
                ? data.FormDefinition.WIZARD_ATTRIBUTES_LOGIC
                : wizardState.wizard && wizardState.wizard.wizardAttributesLogic
                  ? wizardState.wizard.wizardAttributesLogic
                  : undefined,
              mode: data.FormDefinition.MODE,
            }),
          )
        })
    },
    (err) => {
      console.error(err)
      dispatch(setWizardErrorMessage(err.message || null, err.title))
      dispatch(setWizardLoadFailed(true))
      dispatch(toggleWizardLoading(false))
    },
  )
}

export const fetchAndPopulateDraft = (draftId, url) => (dispatch, getState) => {
  dispatch(toggleWizardLoading(true))
  api.LOAD_DRAFT_BY_ID(
    draftId,
    (data) => {
      const record = data.DRAFT
      api.LOAD_WIZARD_INJECT_METADATA(
        record.FORM_DEFINITION_ID,
        record.AMENDMENT_NUM,
        true,
        (wizard) => {
          const stepsNew = data.DRAFT.WIZARD_STATE.wizard.steps
            ? {
                ...wizard.STEPS_JSON,
                steps: Object.fromEntries(
                  Object.entries(wizard.STEPS_JSON.steps).map(([k, v], i) => {
                    const objectToValidate = Object.entries(
                      data.DRAFT.WIZARD_STATE.wizard.steps,
                    ).find((s) => s[0] === k)
                    return [
                      k,
                      {
                        ...v,
                        isValid: objectToValidate ? objectToValidate[1].isValid : null,
                      },
                    ]
                  }),
                ),
              }
            : wizard.STEPS_JSON
          const steps = stepsNew
          const draft = record.WIZARD_JSON
          const wizardState = record.WIZARD_STATE
          const parsedWizard = CreateInstancesFromJson(steps, draft.FIELD_VALUES, {
            forceAllowPrepopulation: true,
          })
          const meta = xml2Json.xml2js(wizard.METADATA)
            ? xml2Json.xml2js(wizard.METADATA).METADATA
            : {}
          prefetchRequiredData(parsedWizard.fields)
            .then((fields) => preFetchMergeCodes(fields, meta))
            .then((fields) => {
              const publicWizard =
                url.includes('/forms/') ||
                url.includes('/amend/') ||
                (url.includes('/draft/') && !url.includes('/office/')) ||
                url.includes('/view/') ||
                url.includes('/correct/')
              const staffWizard = url.includes('/office/')

              const filteredStepOrder = publicWizard
                ? parsedWizard.stepOrder.filter((key) =>
                    parsedWizard.steps[key].visibleTo.includes('Public'),
                  )
                : staffWizard
                  ? parsedWizard.stepOrder.filter((key) =>
                      parsedWizard.steps[key].visibleTo.includes('SOSStaff'),
                    )
                  : parsedWizard.stepOrder

              let filteredSteps = publicWizard
                ? Object.fromEntries(
                    Object.entries(parsedWizard.steps).filter(([key, val]) =>
                      val.visibleTo.includes('Public'),
                    ),
                  )
                : staffWizard
                  ? Object.fromEntries(
                      Object.entries(parsedWizard.steps).filter(([key, val]) =>
                        val.visibleTo.includes('SOSStaff'),
                      ),
                    )
                  : parsedWizard.steps

              const filteredSections = publicWizard
                ? Object.fromEntries(
                    Object.entries(parsedWizard.sections).filter(([key, val]) =>
                      val.visibleTo.includes('Public'),
                    ),
                  )
                : staffWizard
                  ? Object.fromEntries(
                      Object.entries(parsedWizard.sections).filter(([key, val]) =>
                        val.visibleTo.includes('SOSStaff'),
                      ),
                    )
                  : parsedWizard.sections

              Object.entries(filteredSteps).forEach(
                (step) =>
                  (step[1].sectionOrder = step[1].sectionOrder.filter((section) =>
                    Object.keys(filteredSections).includes(section),
                  )),
              )
              dispatch(
                populateWizard({
                  ...wizardState,
                  title: wizard.TITLE,
                  stepOrder: filteredStepOrder,
                  steps: filteredSteps,
                  sections: filteredSections,
                  fields,
                  description: wizard.DESCR,
                  sourceId: wizard.SOURCE_ID,
                  meta,
                  docTitleSrc: wizard.DOC_TITLE_SRC,
                  draftId,
                  amendmentNum: wizardState.wizard.amendmentNum,
                  docId: parsedWizard.docId,
                  original: wizardState.diff.original,
                  originalByFieldId: wizardState.diff.originalByFieldId,
                  formTitle: wizard.FORM_TITLE,
                  requireStaffEntry: data.REQUIRE_STAFF_ENTRY_YN,
                  showSignatures: wizard.SHOW_SIGNATURES,
                  internalReviewRequired: wizard.INTERNAL_REVIEW_REQUIRED_YN,
                  requirePrintMailMethod: data.PRINT_MAIL_REQUIRED_YN,
                  onlyRenderChangedFieldsOnForm: wizard.ONLY_SHOW_CHANGED_FIELDS_ON_FORM_YN,
                  formDefinitionId: record.FORM_DEFINITION_ID,
                  category: record.CATEGORY,
                  noLoginRequired: record.PUBLIC_YN,
                  feeDescription: wizard.FEE_DESCR,
                  draftsDisabled: wizard.DRAFTS_DISABLED,
                  backOfficeOnly: wizard.BACK_OFFICE_ONLY_YN,
                  thirdPartySigningAllowed: wizard.AUTHORIZED_THIRD_PARTY_YN,
                  globalVariables: wizard.GLOBAL_VARIABLES ? wizard.GLOBAL_VARIABLES : undefined,
                  wizardAttributesLogic: wizard.WIZARD_ATTRIBUTES_LOGIC
                    ? wizard.WIZARD_ATTRIBUTES_LOGIC
                    : wizardState.wizard && wizardState.wizard.wizardAttributesLogic
                      ? wizardState.wizard.wizardAttributesLogic
                      : undefined,
                }),
              )
            })
        },
        (err) => {
          console.error(err)
          dispatch(setWizardErrorMessage(err.message || null, err.title))
          dispatch(setWizardLoadFailed(true))
          dispatch(toggleWizardLoading(false))
        },
        !!record.SOURCE_TYPE_ID && !!record.SOURCE_ID
          ? `?sourceTypeId=${record.SOURCE_TYPE_ID}&sourceId=${record.SOURCE_ID}`
          : '',
        url.includes('miscellaneous'),
      )
    },
    (err) => {
      console.error(err)
      dispatch(setWizardErrorMessage(err.message || null, err.title))
      dispatch(setWizardLoadFailed(true))
      dispatch(toggleWizardLoading(false))
    },
  )
}

export const fetchAndPopulateAmendmentStaffDraft =
  ({ ImageGroupID, formDefinitionId, amendmentNum, correctionMode = false, url }) =>
  (dispatch) => {    
    dispatch(toggleWizardLoading(true))
    api.LOAD_STAFF_WIZARD_DRAFT_BY_ID(
      ImageGroupID,
      (data) => {
        const draft = JSON.parse(data.WizardJson)
        const wizardState = JSON.parse(data.WizardState)
        api.LOAD_FILING_BY_ID(
          amendmentNum,
          data.FormDefinition.CATEGORY,
          correctionMode,
          async (amendment) => {
            let steps = data.FormDefinition.STEPS_JSON
            let amendmentValues = {
              ...JSON.parse(amendment.JSON).FIELD_VALUES,
              ...(draft.FIELD_VALUES || {}),
            }
            let showSignatures =
              CONDITIONALLY_SHOW_SIGNATURES_FOR_CORRECTIONS && data.FormDefinition.SHOW_SIGNATURES
                ? false
                : true
            let parsedWizard = CreateInstancesFromJson(steps, amendmentValues, {
              clearSignatures: showSignatures,
              correctionMode,
            })
            const FIELD_META = JSON.parse(amendment.JSON).FIELD_META
            const meta =
              wizardState.wizard && wizardState.wizard.meta ? wizardState.wizard.meta : {}
            let prevFields = null
            if (amendment.PREV_JSON) {
              let {
                FIELD_VALUES: formValues,
                METADATA: { DYNAMIC_DATA },
              } = JSON.parse(amendment.PREV_JSON)
              let previousWizard = CreateInstancesFromJson(
                steps,
                { ...DYNAMIC_DATA, ...formValues },
                { forceAllowPrepopulation: true },
              )
              prevFields = await preFetchMergeCodes(
                await prefetchRequiredData(previousWizard.fields),
              )
            }
            prefetchRequiredData(parsedWizard.fields)
              .then((fields) => preFetchMergeCodes(fields, meta))
              .then((fields) => {
                const publicWizard =
                  url.includes('/forms/') ||
                  url.includes('/amend/') ||
                  (url.includes('/draft/') && !url.includes('/office/')) ||
                  url.includes('/view/') ||
                  url.includes('/correct/')
                const staffWizard = url.includes('/office/')

                const original = getOriginalValues(prevFields || fields)
                const originalByFieldId = getOriginalByFieldId(prevFields || fields)
                const fieldsRequirePrintMailMethod = calculatePrintMailMethod(fields)

                const filteredStepOrder = publicWizard
                  ? parsedWizard.stepOrder.filter((key) =>
                      parsedWizard.steps[key].visibleTo.includes('Public'),
                    )
                  : staffWizard
                    ? parsedWizard.stepOrder.filter((key) =>
                        parsedWizard.steps[key].visibleTo.includes('SOSStaff'),
                      )
                    : parsedWizard.stepOrder

                let filteredSteps = publicWizard
                  ? Object.fromEntries(
                      Object.entries(parsedWizard.steps).filter(([key, val]) =>
                        val.visibleTo.includes('Public'),
                      ),
                    )
                  : staffWizard
                    ? Object.fromEntries(
                        Object.entries(parsedWizard.steps).filter(([key, val]) =>
                          val.visibleTo.includes('SOSStaff'),
                        ),
                      )
                    : parsedWizard.steps

                const filteredSections = publicWizard
                  ? Object.fromEntries(
                      Object.entries(parsedWizard.sections).filter(([key, val]) =>
                        val.visibleTo.includes('Public'),
                      ),
                    )
                  : staffWizard
                    ? Object.fromEntries(
                        Object.entries(parsedWizard.sections).filter(([key, val]) =>
                          val.visibleTo.includes('SOSStaff'),
                        ),
                      )
                    : parsedWizard.sections

                Object.entries(filteredSteps).forEach(
                  (step) =>
                    (step[1].sectionOrder = step[1].sectionOrder.filter((section) => {
                      return Object.keys(filteredSections).includes(section)
                    })),
                )

                setServerSelectSelectedLabel(fields, FIELD_META)

                dispatch(
                  populateWizard({
                    ...wizardState,
                    formDefinitionId: formDefinitionId,
                    title: data.FormDefinition.TITLE,
                    stepOrder: filteredStepOrder,
                    steps: filteredSteps,
                    sections: filteredSections,
                    fields,
                    description: data.FormDefinition.DESCR,
                    sourceId: data.FormDefinition.SOURCE_ID,
                    amendmentNum,
                    draftId: false,
                    category: data.FormDefinition.CATEGORY,
                    meta,
                    docTitleSrc: data.FormDefinition.DOC_TITLE_SRC,
                    mode: data.FormDefinition.MODE,
                    original,
                    originalByFieldId,
                    docId: parsedWizard.docId,
                    correctionMode,
                    showDiff: amendment.SHOW_DIFF_YN,
                    formTitle: data.FormDefinition.FORM_TITLE,
                    requireStaffEntry: data.FormDefinition.REQUIRE_STAFF_ENTRY_YN,
                    showSignatures: data.FormDefinition.SHOW_SIGNATURES,
                    internalReviewRequired:
                      meta.INTERNAL_REVIEW_REQUIRED_YN ||
                      data.FormDefinition.INTERNAL_REVIEW_REQUIRED_YN,
                    onlyRenderChangedFieldsOnForm:
                      data.FormDefinition.ONLY_SHOW_CHANGED_FIELDS_ON_FORM_YN,
                    requirePrintMailMethod: amendment.PRINT_MAIL_REQUIRED_YN,
                    fieldsRequirePrintMailMethod,
                    noLoginRequired: data.FormDefinition.PUBLIC_YN,
                    feeDescription: data.FormDefinition.FEE_DESCR,
                    canChangeCorrectionEntryType: amendment.CAN_CHANGE_CORRECTION_ENTRY_TYPE,
                    draftsDisabled: data.FormDefinition.DRAFTS_DISABLED,
                    backOfficeOnly: data.FormDefinition.BACK_OFFICE_ONLY_YN,
                    thirdPartySigningAllowed: data.FormDefinition.AUTHORIZED_THIRD_PARTY_YN,
                    globalVariables: data.FormDefinition.GLOBAL_VARIABLES
                      ? data.FormDefinition.GLOBAL_VARIABLES
                      : undefined,
                    wizardAttributesLogic: data.FormDefinition.WIZARD_ATTRIBUTES_LOGIC
                      ? data.FormDefinition.WIZARD_ATTRIBUTES_LOGIC
                      : wizardState.wizard && wizardState.wizard.wizardAttributesLogic
                        ? wizardState.wizard.wizardAttributesLogic
                        : undefined,
                  }),
                )
              })
          },
          (err) => {
            console.error(err)
            dispatch(setWizardErrorMessage(err.message || null, err.title))
            dispatch(setWizardLoadFailed(true))
            dispatch(toggleWizardLoading(false))
          },
        )
      },
      (err) => {
        console.error(err)
        dispatch(setWizardErrorMessage(err.message || null, err.title))
        dispatch(setWizardLoadFailed(true))
        dispatch(toggleWizardLoading(false))
      },
    )
  }

export const fetchAndPopulateStaffFilingCorrection =
  ({ FilingNum, RecordNum, url, formDefinitionId }) =>
  (dispatch) => {
    dispatch(toggleWizardLoading(true))

    api.LOAD_WIZARD_INJECT_METADATA(
      formDefinitionId,
      FilingNum,
      false,
      (wizard) => {
        api.LOAD_STAFF_FILING_CORRECTION_WIZARD(
          FilingNum,
          RecordNum,
          async (data) => {
            const newStuff = JSON.parse(data.JSON)
            const steps = wizard.STEPS_JSON
            const wizardState = wizard.WIZARD_STATE
            const parsedWizard = CreateInstancesFromJson(steps, newStuff.FIELD_VALUES, {
              forceAllowPrepopulation: false,
            })
            const meta = xml2Json.xml2js(wizard.METADATA)
              ? xml2Json.xml2js(wizard.METADATA).METADATA
              : {}
            let prevFields = null
            if (data.PREV_JSON) {
              let {
                FIELD_VALUES: formValues,
                METADATA: { DYNAMIC_DATA },
              } = JSON.parse(data.PREV_JSON)
              let previousWizard = CreateInstancesFromJson(
                steps,
                { ...DYNAMIC_DATA, ...formValues },
                { forceAllowPrepopulation: true },
              )
              prevFields = await preFetchMergeCodes(
                await prefetchRequiredData(previousWizard.fields),
              )
            }
            prefetchRequiredData(parsedWizard.fields)
              .then((fields) => preFetchMergeCodes(fields, meta))
              .then((fields) => {
                const publicWizard =
                  url.includes('/forms/') ||
                  url.includes('/amend/') ||
                  (url.includes('/draft/') && !url.includes('/office/')) ||
                  url.includes('/view/') ||
                  url.includes('/correct/')
                const staffWizard = url.includes('/office/')

                const filteredStepOrder = publicWizard
                  ? parsedWizard.stepOrder.filter((key) =>
                      parsedWizard.steps[key].visibleTo.includes('Public'),
                    )
                  : staffWizard
                    ? parsedWizard.stepOrder.filter((key) =>
                        parsedWizard.steps[key].visibleTo.includes('SOSStaff'),
                      )
                    : parsedWizard.stepOrder

                let filteredSteps = publicWizard
                  ? Object.fromEntries(
                      Object.entries(parsedWizard.steps).filter(([key, val]) =>
                        val.visibleTo.includes('Public'),
                      ),
                    )
                  : staffWizard
                    ? Object.fromEntries(
                        Object.entries(parsedWizard.steps).filter(([key, val]) =>
                          val.visibleTo.includes('SOSStaff'),
                        ),
                      )
                    : parsedWizard.steps

                const filteredSections = publicWizard
                  ? Object.fromEntries(
                      Object.entries(parsedWizard.sections).filter(([key, val]) =>
                        val.visibleTo.includes('Public'),
                      ),
                    )
                  : staffWizard
                    ? Object.fromEntries(
                        Object.entries(parsedWizard.sections).filter(([key, val]) =>
                          val.visibleTo.includes('SOSStaff'),
                        ),
                      )
                    : parsedWizard.sections

                Object.entries(filteredSteps).forEach(
                  (step) =>
                    (step[1].sectionOrder = step[1].sectionOrder.filter((section) =>
                      Object.keys(filteredSections).includes(section),
                    )),
                )
                dispatch(
                  populateWizard({
                    ...wizardState,
                    title: wizard.TITLE,
                    stepOrder: filteredStepOrder,
                    steps: filteredSteps,
                    sections: filteredSections,
                    fields,
                    description: wizard.DESCR,
                    sourceId: wizard.SOURCE_ID,
                    meta,
                    docTitleSrc: wizard.DOC_TITLE_SRC,
                    // amendmentNum: wizardState.wizard.amendmentNum,
                    docId: parsedWizard.docId,
                    original: getOriginalValues(prevFields || fields),
                    originalByFieldId: getOriginalByFieldId(prevFields || fields),
                    formTitle: wizard.FORM_TITLE,
                    requireStaffEntry: wizard.REQUIRE_STAFF_ENTRY_YN,
                    showSignatures: wizard.SHOW_SIGNATURES,
                    internalReviewRequired: wizard.INTERNAL_REVIEW_REQUIRED_YN,
                    requirePrintMailMethod: wizard.PRINT_MAIL_REQUIRED_YN,
                    onlyRenderChangedFieldsOnForm: wizard.ONLY_SHOW_CHANGED_FIELDS_ON_FORM_YN,
                    formDefinitionId: wizard.FORM_DEFINITION_ID,
                    category: wizard.CATEGORY,
                    noLoginRequired: wizard.PUBLIC_YN,
                    feeDescription: wizard.FEE_DESCR,
                    draftsDisabled: wizard.DRAFTS_DISABLED,
                    backOfficeOnly: wizard.BACK_OFFICE_ONLY_YN,
                    thirdPartySigningAllowed: wizard.AUTHORIZED_THIRD_PARTY_YN,
                    globalVariables: wizard.GLOBAL_VARIABLES ? wizard.GLOBAL_VARIABLES : undefined,
                    wizardAttributesLogic: wizard.WIZARD_ATTRIBUTES_LOGIC
                      ? wizard.WIZARD_ATTRIBUTES_LOGIC
                      : wizardState.wizard && wizardState.wizard.wizardAttributesLogic
                        ? wizardState.wizard.wizardAttributesLogic
                        : undefined,
                  }),
                )
              })
          },
          (err) => {
            console.error(err)
            dispatch(setWizardErrorMessage(err.message || null, err.title))
            dispatch(setWizardLoadFailed(true))
            dispatch(toggleWizardLoading(false))
          },
        )
      },
      (err) => {
        console.error(err)
        dispatch(setWizardErrorMessage(err.message || null, err.title))
        dispatch(setWizardLoadFailed(true))
        dispatch(toggleWizardLoading(false))
      },
    )
  }

export const fetchAndPopulateAmendment =
  ({ formDefinitionId, amendmentNum, correctionMode = false, url }) =>
  (dispatch) => {
    dispatch(toggleWizardLoading(true))

    const errorCallback = (err) => {
      console.error(err)
      dispatch(setWizardErrorMessage(err.message || null, err.title))
      dispatch(setWizardLoadFailed(true))
      dispatch(toggleWizardLoading(false))
    }

    api.LOAD_WIZARD_INJECT_METADATA(
      formDefinitionId,
      amendmentNum,
      !correctionMode,
      (wizard) => {
        let steps = wizard.STEPS_JSON

        const populate = (amendmentValues, meta, prevFields, amendment) => {
          let showSignatures =
            CONDITIONALLY_SHOW_SIGNATURES_FOR_CORRECTIONS && wizard.SHOW_SIGNATURES ? false : true
          let parsedWizard = CreateInstancesFromJson(steps, amendmentValues, {
            clearSignatures: showSignatures,
            correctionMode,
          })
          prefetchRequiredData(parsedWizard.fields)
            .then((fields) => preFetchMergeCodes(fields, meta))
            .then((fields) => {
              const publicWizard =
                url.includes('/forms/') ||
                url.includes('/amend/') ||
                (url.includes('/draft/') && !url.includes('/office/')) ||
                url.includes('/view/') ||
                url.includes('/correct/')
              const staffWizard = url.includes('/office/')

              const original = getOriginalValues(prevFields || fields)
              const originalByFieldId = getOriginalByFieldId(prevFields || fields)
              const fieldsRequirePrintMailMethod = calculatePrintMailMethod(fields)

              const filteredStepOrder = publicWizard
                ? parsedWizard.stepOrder.filter((key) =>
                    parsedWizard.steps[key].visibleTo.includes('Public'),
                  )
                : staffWizard
                  ? parsedWizard.stepOrder.filter((key) =>
                      parsedWizard.steps[key].visibleTo.includes('SOSStaff'),
                    )
                  : parsedWizard.stepOrder

              let filteredSteps = publicWizard
                ? Object.fromEntries(
                    Object.entries(parsedWizard.steps).filter(([key, val]) =>
                      val.visibleTo.includes('Public'),
                    ),
                  )
                : staffWizard
                  ? Object.fromEntries(
                      Object.entries(parsedWizard.steps).filter(([key, val]) =>
                        val.visibleTo.includes('SOSStaff'),
                      ),
                    )
                  : parsedWizard.steps

              const filteredSections = publicWizard
                ? Object.fromEntries(
                    Object.entries(parsedWizard.sections).filter(([key, val]) =>
                      val.visibleTo.includes('Public'),
                    ),
                  )
                : staffWizard
                  ? Object.fromEntries(
                      Object.entries(parsedWizard.sections).filter(([key, val]) =>
                        val.visibleTo.includes('SOSStaff'),
                      ),
                    )
                  : parsedWizard.sections

              Object.entries(filteredSteps).forEach(
                (step) =>
                  (step[1].sectionOrder = step[1].sectionOrder.filter((section) => {
                    return Object.keys(filteredSections).includes(section)
                  })),
              )

              setServerSelectSelectedLabel(fields, amendmentValues)

              dispatch(
                populateWizard({
                  formDefinitionId: formDefinitionId,
                  title: wizard.TITLE,
                  stepOrder: filteredStepOrder,
                  steps: filteredSteps,
                  sections: filteredSections,
                  fields,
                  description: wizard.DESCR,
                  sourceId: wizard.SOURCE_ID,
                  amendmentNum,
                  draftId: false,
                  category: wizard.CATEGORY,
                  meta,
                  docTitleSrc: wizard.DOC_TITLE_SRC,
                  mode: wizard.MODE,
                  original,
                  originalByFieldId,
                  docId: parsedWizard.docId,
                  correctionMode,
                  showDiff: amendment && amendment.SHOW_DIFF_YN,
                  formTitle: wizard.FORM_TITLE,
                  requireStaffEntry: wizard.REQUIRE_STAFF_ENTRY_YN,
                  showSignatures: wizard.SHOW_SIGNATURES,
                  internalReviewRequired:
                    meta.INTERNAL_REVIEW_REQUIRED_YN || wizard.INTERNAL_REVIEW_REQUIRED_YN,
                  onlyRenderChangedFieldsOnForm: wizard.ONLY_SHOW_CHANGED_FIELDS_ON_FORM_YN,
                  requirePrintMailMethod: amendment && amendment.PRINT_MAIL_REQUIRED_YN,
                  fieldsRequirePrintMailMethod,
                  noLoginRequired: wizard.PUBLIC_YN,
                  feeDescription: wizard.FEE_DESCR,
                  canChangeCorrectionEntryType: amendment && amendment.CAN_CHANGE_CORRECTION_ENTRY_TYPE,
                  draftsDisabled: wizard.DRAFTS_DISABLED,
                  backOfficeOnly: wizard.BACK_OFFICE_ONLY_YN,
                  thirdPartySigningAllowed: wizard.AUTHORIZED_THIRD_PARTY_YN,
                  globalVariables: wizard.GLOBAL_VARIABLES ? wizard.GLOBAL_VARIABLES : undefined,
                  wizardAttributesLogic: wizard.WIZARD_ATTRIBUTES_LOGIC
                    ? wizard.WIZARD_ATTRIBUTES_LOGIC
                    : undefined,
                }),
              )
            })
        }

        if (url.includes('/miscellaneous/')) {
          populate(
            wizard.FIELD_VALUES || {},
            xml2Json.xml2js(wizard.METADATA).METADATA || {},
            null,
            { SHOW_DIFF_YN: wizard.SHOW_DIFF_YN },
          )
          return
        }

        api.LOAD_FILING_BY_ID(
          amendmentNum,
          wizard.CATEGORY,
          correctionMode,
          async (amendment) => {
            let amendmentValues = {
              ...(wizard.FIELD_VALUES || {}),
              ...JSON.parse(amendment.JSON).FIELD_VALUES,
            }
            // const FIELD_META = JSON.parse(amendment.JSON).FIELD_META
            const meta =
              xml2Json.xml2js(wizard.METADATA) || JSON.parse(amendment.JSON).METADATA
                ? correctionMode
                  ? JSON.parse(amendment.JSON).METADATA
                  : xml2Json.xml2js(wizard.METADATA).METADATA
                : {}
            let prevFields = null
            if (amendment.PREV_JSON) {
              let {
                FIELD_VALUES: formValues,
                METADATA: { DYNAMIC_DATA },
              } = JSON.parse(amendment.PREV_JSON)
              let previousWizard = CreateInstancesFromJson(
                steps,
                { ...DYNAMIC_DATA, ...formValues },
                { forceAllowPrepopulation: true },
              )
              prevFields = await preFetchMergeCodes(
                await prefetchRequiredData(previousWizard.fields),
              )
            }
            populate(amendmentValues, meta, prevFields, amendment)
          },
          errorCallback,
        )
      },
      errorCallback,
      '',
      url.includes('miscellaneous')
    )
  }

export const fetchAndPopulateReadOnlyWizard =
  ({ formDefinitionId, amendmentNum, url }) =>
  (dispatch) => {
    dispatch(toggleWizardLoading(true))
    api.LOAD_WIZARD_INJECT_METADATA(
      formDefinitionId,
      amendmentNum,
      false,
      (wizard) => {
        api.LOAD_READONLY_FILING_BY_ID(
          amendmentNum,
          wizard.CATEGORY,
          async (amendment) => {
            let steps = wizard.STEPS_JSON
            let precalculatedValues = amendment.PRECALCULATED_VALUES
              ? JSON.parse(amendment.PRECALCULATED_VALUES)
              : {}
            let {
              FIELD_VALUES: formValues,
              METADATA: { DYNAMIC_DATA },
              FIELD_META,
            } = JSON.parse(amendment.JSON)
            let parsedWizard = CreateInstancesFromJson(
              steps,
              { ...precalculatedValues, ...DYNAMIC_DATA, ...formValues },
              { forceAllowPrepopulation: true },
            )
            let prevFields = null
            if (amendment.PREV_JSON) {
              let {
                FIELD_VALUES: formValues,
                METADATA: { DYNAMIC_DATA },
              } = JSON.parse(amendment.PREV_JSON)
              let previousWizard = CreateInstancesFromJson(
                steps,
                { ...precalculatedValues, ...DYNAMIC_DATA, ...formValues },
                { forceAllowPrepopulation: true },
              )
              prevFields = await preFetchMergeCodes(
                await prefetchRequiredData(previousWizard.fields),
              )
            }
            const meta = xml2Json.xml2js(wizard.METADATA)
              ? xml2Json.xml2js(wizard.METADATA).METADATA
              : {}
            prefetchRequiredData(parsedWizard.fields)
              .then((fields) => preFetchMergeCodes(fields, meta))
              .then((fields) => {
                const original = getOriginalValues(prevFields || fields)
                const originalByFieldId = getOriginalByFieldId(prevFields || fields)
                const fieldsRequirePrintMailMethod = calculatePrintMailMethod(fields)

                const publicWizard =
                  url.includes('/forms/') ||
                  url.includes('/amend/') ||
                  (url.includes('/draft/') && !url.includes('/office/')) ||
                  url.includes('/view/') ||
                  url.includes('/correct/')
                const staffWizard = url.includes('/office/')

                const filteredStepOrder = publicWizard
                  ? parsedWizard.stepOrder.filter((key) =>
                      parsedWizard.steps[key].visibleTo.includes('Public'),
                    )
                  : staffWizard
                    ? parsedWizard.stepOrder.filter((key) =>
                        parsedWizard.steps[key].visibleTo.includes('SOSStaff'),
                      )
                    : parsedWizard.stepOrder

                let filteredSteps = publicWizard
                  ? Object.fromEntries(
                      Object.entries(parsedWizard.steps).filter(([key, val]) =>
                        val.visibleTo.includes('Public'),
                      ),
                    )
                  : staffWizard
                    ? Object.fromEntries(
                        Object.entries(parsedWizard.steps).filter(([key, val]) =>
                          val.visibleTo.includes('SOSStaff'),
                        ),
                      )
                    : parsedWizard.steps

                const filteredSections = publicWizard
                  ? Object.fromEntries(
                      Object.entries(parsedWizard.sections).filter(([key, val]) =>
                        val.visibleTo.includes('Public'),
                      ),
                    )
                  : staffWizard
                    ? Object.fromEntries(
                        Object.entries(parsedWizard.sections).filter(([key, val]) =>
                          val.visibleTo.includes('SOSStaff'),
                        ),
                      )
                    : parsedWizard.sections

                Object.entries(filteredSteps).forEach(
                  (step) =>
                    (step[1].sectionOrder = step[1].sectionOrder.filter((section) => {
                      return Object.keys(filteredSections).includes(section)
                    })),
                )

                setServerSelectSelectedLabel(fields, FIELD_META)

                dispatch(
                  populateWizard({
                    formDefinitionId: formDefinitionId,
                    title: wizard.TITLE,
                    stepOrder: filteredStepOrder,
                    steps: filteredSteps,
                    sections: filteredSections,
                    fields,
                    description: wizard.DESCR,
                    sourceId: wizard.SOURCE_ID,
                    amendmentNum,
                    original,
                    originalByFieldId,
                    readOnly: true,
                    meta,
                    docTitleSrc: wizard.DOC_TITLE_SRC,
                    draftId: false,
                    docId: parsedWizard.docId,
                    category: wizard.CATEGORY,
                    mode: wizard.MODE,
                    correctionMode: true,
                    formTitle: wizard.FORM_TITLE,
                    requireStaffEntry: wizard.REQUIRE_STAFF_ENTRY_YN,
                    showSignatures: wizard.SHOW_SIGNATURES,
                    internalReviewRequired: wizard.INTERNAL_REVIEW_REQUIRED_YN,
                    requirePrintMailMethod: wizard.PRINT_MAIL_REQUIRED_YN,
                    fieldsRequirePrintMailMethod,
                    onlyRenderChangedFieldsOnForm: wizard.ONLY_SHOW_CHANGED_FIELDS_ON_FORM_YN,
                    noLoginRequired: wizard.PUBLIC_YN,
                    feeDescription: wizard.FEE_DESCR,
                    canChangeEntryType: amendment.CAN_CHANGE_ENTRY_TYPE_YN,
                    draftsDisabled: wizard.DRAFTS_DISABLED,
                    backOfficeOnly: wizard.BACK_OFFICE_ONLY_YN,
                    thirdPartySigningAllowed: wizard.AUTHORIZED_THIRD_PARTY_YN,
                    globalVariables: wizard.GLOBAL_VARIABLES ? wizard.GLOBAL_VARIABLES : undefined,
                    wizardAttributesLogic: wizard.WIZARD_ATTRIBUTES_LOGIC
                      ? wizard.WIZARD_ATTRIBUTES_LOGIC
                      : undefined,
                  }),
                )
              })
          },
          (err) => {
            console.error(err)
            dispatch(setWizardErrorMessage(err.message || null, err.title))
            dispatch(setWizardLoadFailed(true))
            dispatch(toggleWizardLoading(false))
          },
        )
      },
      (err) => {
        console.error(err)
        dispatch(setWizardErrorMessage(err.message || null, err.title))
        dispatch(setWizardLoadFailed(true))
        dispatch(toggleWizardLoading(false))
      },
      '',
      url.includes('miscellaneous')
    )
  }

export const fetchAndPopulateSignatures = (token) => (dispatch) => {
  dispatch(toggleWizardLoading(true))
  api.GET_SIGNATURE_FORM_BY_TOKEN(
    token,
    ({ AMENDMENT: amendment, FORM_DEFINITION: wizard, SIGNATURE: signature }) => {
      if (signature.SIGNED_DATE !== null) {
        dispatch(toggleWizardLoading())
        dispatch(
          setGenericSuccess({
            title: 'Successfully submitted',
            message:
              'Your signature was successfully saved. You may now safely close your browser window.',
          }),
        )
      } else {
        let steps = wizard.STEPS_JSON
        let amendmentValues = JSON.parse(amendment.WIZARD_JSON).FIELD_VALUES
        let parsedWizard = CreateInstancesFromJson(steps, amendmentValues)
        const meta = xml2Json.xml2js(wizard.METADATA)
          ? xml2Json.xml2js(wizard.METADATA).METADATA
          : {}
        prefetchRequiredData(parsedWizard.fields)
          .then((fields) => preFetchMergeCodes(fields, meta))
          .then((fields) => {
            const original = getOriginalValues(fields)
            const originalByFieldId = getOriginalByFieldId(fields)
            const fieldsRequirePrintMailMethod = calculatePrintMailMethod(fields)
            dispatch(
              populateWizard({
                formDefinitionId: wizard.FORM_DEFINITION_ID,
                title: wizard.TITLE,
                stepOrder: parsedWizard.stepOrder,
                steps: parsedWizard.steps,
                sections: parsedWizard.sections,
                fields,
                description: wizard.DESCR,
                sourceId: wizard.SOURCE_ID,
                amendmentNum: amendment.AMENDMENT_NUM,
                draftId: false,
                category: wizard.CATEGORY,
                meta,
                docTitleSrc: wizard.DOC_TITLE_SRC,
                mode: 'signature',
                original,
                originalByFieldId,
                docId: parsedWizard.docId,
                correctionMode: false,
                showDiff: amendment.SHOW_DIFF_YN,
                formTitle: wizard.FORM_TITLE,
                requireStaffEntry: wizard.REQUIRE_STAFF_ENTRY_YN,
                showSignatures: wizard.SHOW_SIGNATURES,
                internalReviewRequired:
                  meta.INTERNAL_REVIEW_REQUIRED_YN || wizard.INTERNAL_REVIEW_REQUIRED_YN,
                onlyRenderChangedFieldsOnForm: wizard.ONLY_SHOW_CHANGED_FIELDS_ON_FORM_YN,
                requirePrintMailMethod: amendment.PRINT_MAIL_REQUIRED_YN,
                fieldsRequirePrintMailMethod,
                noLoginRequired: true,
                feeDescription: wizard.FEE_DESCR,
                canChangeCorrectionEntryType: amendment.CAN_CHANGE_CORRECTION_ENTRY_TYPE,
                draftsDisabled: true,
                backOfficeOnly: false,
                globalVariables: wizard.GLOBAL_VARIABLES ? wizard.GLOBAL_VARIABLES : undefined,
                wizardAttributesLogic: wizard.WIZARD_ATTRIBUTES_LOGIC
                  ? wizard.WIZARD_ATTRIBUTES_LOGIC
                  : undefined,
                signature: {
                  ...signature,
                  SIGNATURE_JSON: JSON.parse(signature.SIGNATURE_JSON),
                },
              }),
            )
            dispatch(resetWizardSignatureValues())
          })
      }
    },
    (err) => {
      console.error(err)
      dispatch(setWizardErrorMessage(err.message || null, err.title))
      dispatch(setWizardLoadFailed(true))
      dispatch(toggleWizardLoading(false))
    },
  )
}

const calculatePrintMailMethod = (fields) => {
  const requirePrintMailMethod = new Set()
  const fieldsArray = Object.keys(fields).map((fieldId) => fields[fieldId])
  fieldsArray.forEach((field) => {
    switch (field.type) {
      case 'repeater':
        switch (field.printMailRequiredIf) {
          case '>1':
            if (field.value.length > 1) requirePrintMailMethod.add(field.fieldId)
            break
          case '>2':
            if (field.value.length > 2) requirePrintMailMethod.add(field.fieldId)
            break
        }
        break
      default:
    }
  })
  return requirePrintMailMethod
}

const getOriginalValues = (fields) => {
  let originalValues = {}
  Object.keys(fields).forEach(
    (fieldId) => (originalValues[fields[fieldId].name] = fields[fieldId].value),
  )
  return originalValues
}
const getOriginalByFieldId = (fields) => {
  let originalValues = {}
  Object.keys(fields).forEach((fieldId) => {
    originalValues[fieldId] = fields[fieldId].value
  })
  return originalValues
}

const setServerSelectSelectedLabel = (fields, fieldMeta) => {
  Object.entries(fields)
    .filter((field) => field[1].type === 'serverSelect2')
    .forEach((f) => {
      if (f[1].selectedOptionLabel === '0') {
        if (fieldMeta && fieldMeta[f[1].name]) {
          fields[f[0]] = { ...fields[f[0]], selectedOptionLabel: fieldMeta[f[1].name].displayValue }
        } else if (f[1].parent && f[1].parent.type === 'REPEATER') {
          const valueFromParent =
            fields[f[1].parent.id].value.length > 0
              ? fields[f[1].parent.id].value.find((val) => Object.keys(val).includes(f[0]))[f[0]]
              : null
          if (valueFromParent && f[1].options.find((opt) => opt.value === valueFromParent)) {
            fields[f[0]] = {
              ...fields[f[0]],
              selectedOptionLabel: f[1].options.find((opt) => opt.value === valueFromParent).label,
            }
          }
        }
      }
    })
}

const prefetchRequiredData = (fields) =>
  new Promise((resolve, reject) => {
    const fieldsArray = objectToArray(fields)
    let promises = []
    fieldsArray.forEach((field) => {
      const promise = getPromiseByFieldType(field, fields)
      if (promise) {
        promises.push(promise)
      }
    })
    const requiredAppSettingNames = getRequiredAppSettingNames(fieldsArray)
    if (requiredAppSettingNames.length) {
      promises.push(
        new Promise((resolve, reject) => {
          getAppSettingsByName(
            requiredAppSettingNames,
            () => {
              resolve(null)
            },
            () => {
              resolve(null)
            },
          )
        }),
      )
    }
    Promise.all(promises).then((results) => {
      let newFields = { ...fields }
      results
        .filter((x) => x)
        .forEach((f) => {
          newFields = {
            ...newFields,
            [f.fieldId]: f,
          }
        })
      resolve(newFields)
    })
  })

const GROUP_ITEMS_PROMISE_CACHE = {}
const GROUP_SUB_ITEMS_PROMISE_CACHE = {}

const getPromiseByFieldType = (field, fields) => {
  switch (field.type) {
    case 'serverRadio':
    case 'serverSelect':
    case 'ServerMultiComplete':
      const optionsSrcOverride = field.optionsSrcOverride
        ? fields[field.optionsSrcOverride].value
        : field.optionsSrc

      const subOptionsSrcOverride = field.subOptionsSrcOverride
        ? fields[field.subOptionsSrcOverride].value
        : field.subOptionsSrc

      const urlQuery = window.location.search

      const pendingPromise = subOptionsSrcOverride
        ? GROUP_SUB_ITEMS_PROMISE_CACHE[optionsSrcOverride + subOptionsSrcOverride + urlQuery]
        : GROUP_ITEMS_PROMISE_CACHE[optionsSrcOverride + urlQuery]
      if (pendingPromise) {
        return new Promise((resolve, reject) => {
          pendingPromise.then((response) => {
            if (Array.isArray(response)) {
              resolve({ ...field, options: response, optionsFetched: true }, response)
            } else {
              resolve(
                {
                  ...field,
                  options: response.options,
                  optionGroups: response.optionGroups,
                  optionsFetched: true,
                },
                response,
              )
            }
          })
        })
      } else {
        let innerResolver
        let innerPromise = new Promise((resolve, reject) => {
          innerResolver = resolve
        })
        const cachePromise = new Promise((res, rej) => {
          getGroupItems(optionsSrcOverride, subOptionsSrcOverride, field.includeAllOption)
            .then((response) => {
              if (innerResolver) {
                if (Array.isArray(response)) {
                  innerResolver({ ...field, options: response, optionsFetched: true })
                } else {
                  innerResolver({
                    ...field,
                    options: response.options,
                    optionGroups: response.optionGroups,
                    optionsFetched: true,
                  })
                }
              }
              res(response)
            })
            .catch((err) => {
              console.error(err)
              innerResolver(field)
            })
        })
        if (subOptionsSrcOverride) {
          GROUP_SUB_ITEMS_PROMISE_CACHE[optionsSrcOverride + subOptionsSrcOverride + urlQuery] =
            cachePromise
        } else {
          GROUP_ITEMS_PROMISE_CACHE[optionsSrcOverride + urlQuery] = cachePromise
        }
        return innerPromise
      }
    case 'NameQualifiedOrg':
      return new Promise((res, rej) => {
        const sourceId =
          (field.value.SELECTED && field.value.SELECTED.SOURCE_ID) || field.value.SOURCE_ID || 0
        if (sourceId) {
          api.GET_BY_ID(
            field.searchType || 'QualifiedOrganizations',
            sourceId,
            (SELECTED) => {
              res({ ...field, value: { ...field.value, SELECTED } })
            },
            (err) => {
              console.warn(
                `The value for field, ${field.fieldId}:${field.name}, could not be prefetched.`,
              )
              res(field)
            },
          )
        } else {
          res(field)
        }
      })
    case 'SearchControl':
      return new Promise((res, rej) => {
        const sourceId = field.value ? field.value.SOURCE_ID : field.SOURCE_ID
        const SEARCH_TYPE = field.searchType || 'QualifiedOrganizations'
        if (field.multiSelect) {
          let promises = field.value.map(
            (result) =>
              new Promise((resolve, reject) => {
                api.GET_BY_ID(SEARCH_TYPE, result.SOURCE_ID, resolve, reject)
              }),
          )
          Promise.all(promises)
            .then((values) => {
              res({ ...field, value: values })
            })
            .catch((err) => {
              console.error(err)
              res(field)
            })
        } else {
          if (sourceId) {
            api.GET_BY_ID(
              SEARCH_TYPE,
              sourceId,
              (SELECTED) => {
                res({ ...field, value: SELECTED })
              },
              (err) => {
                console.warn(
                  `The value for field, ${field.fieldId}:${field.name}, could not be prefetched.`,
                )
                res(field)
              },
            )
          } else {
            res(field)
          }
        }
      })
    case 'registeredAgentSearch':
      return new Promise((res, rej) => {
        const sourceId = field.value.SELECTED && field.value.SELECTED.SOURCE_ID
        if (sourceId) {
          api.GET_BY_ID(
            'ActiveRegisteredAgents',
            sourceId,
            (SELECTED) => {
              res({ ...field, value: { ...field.value, SELECTED } })
            },
            (err) => {
              console.warn(
                `The value for field, ${field.fieldId}:${field.name}, could not be prefetched.`,
              )
              res(field)
            },
          )
        } else {
          res(field)
        }
      })
    case 'repeater':
      let allRowPromises = field.value.map(
        (row, i) =>
          new Promise((resolve, reject) => {
            let subPromises = []
            Object.keys(row).forEach((fieldId) => {
              let p = getPromiseByFieldType({ ...fields[fieldId], value: row[fieldId] }, fields)
              if (p) {
                subPromises.push(p)
              }
            })
            Promise.all(subPromises).then((cells) => {
              let newRow = { ...row }
              cells.forEach((cell) => {
                newRow = {
                  ...newRow,
                  [cell.fieldId]: cell.value,
                }
              })
              resolve(newRow)
            })
          }),
      )
      return new Promise((resolve, reject) => {
        Promise.all(allRowPromises).then((rows) => {
          resolve({
            ...field,
            value: rows,
          })
        })
      })
    case 'DataTable':
      return new Promise((resolve, reject) => {
        api.GET_DATA_TABLE_BY_ID(
          field.dataSrc,
          field.metadata,
          (table) => {
            resolve({ ...field, value: { template: table.TEMPLATE, rows: table.ROWS } })
          },
          (err) => {
            resolve(field)
          },
        )
      })
    default:
      return
  }
}

const preFetchMergeCodes = (fields, metaData) =>
  new Promise((resolve, reject) => {
    const paragraphFields = getFieldsByType('paragraph', fields)
    if (paragraphFields === null) {
      resolve(fields)
    }
    const allFieldMergeCodes = paragraphFields.reduce(
      (obj, field) => ({ ...obj, ...field.codes }),
      {},
    )
    const codes = Object.keys(allFieldMergeCodes).map((k) => k.replace('{{', '').replace('}}', ''))
    if (codes.length > 0) {
      getMergeCodesByArray(codes, metaData)
        .then((codeValues) => {
          const mergedFields = Object.keys(fields).reduce((obj, fieldId) => {
            const field = fields[fieldId]
            if (field.type === 'paragraph') {
              let value = field.value
              codeValues.forEach((code) => {
                value = value.replace(new RegExp(`{{${code.CODE}}}`, 'g'), code.VALUE)
              })
              return {
                ...obj,
                [fieldId]: {
                  ...field,
                  value,
                },
              }
            } else {
              return { ...obj, [fieldId]: field }
            }
          }, {})
          resolve(mergedFields)
        })
        .catch((err) => {
          console.error(err)
          resolve(fields)
        })
    } else {
      resolve(fields)
    }
  })

const getMergeCodesByArray = (codes, metaData) =>
  new Promise((resolve, reject) => {
    api.GET_MERGE_CODES_BY_ARRAY(codes, metaData, (values) => {
      resolve(values)
    })
  })

export const setFieldAttributes = ({ fieldId, payload }) => ({
  type: 'SET_FIELD_ATTRIBUTES',
  fieldId,
  payload,
})

export const setStepAttributes = ({ stepId, payload }) => ({
  type: 'SET_STEP_ATTRIBUTES',
  stepId,
  payload,
})

export const setWizardAttributes = (payload) => ({
  type: 'SET_WIZARD_ATTRIBUTES',
  payload,
})

export const validateAndSetFieldAttributes =
  ({ fieldId, event, extraPayload, value, setValue, options }, callback) =>
  (dispatch, getState) => {
    const wizardFields = getState().wizard.wizard.fields
    const secondaryValidation = getState().wizard.secondaryValidation
    let rawField = wizardFields[fieldId]
    let field = resolveFieldLogic(rawField, options)
    if (setValue) {
      dispatch(setFieldAttributes({ fieldId, payload: { value, ...extraPayload } }))
    }
    let hasBusinessRules = field.businessRules.length !== 0
    let conditionsSatsified = field.conditions
      ? resolveFieldConditions(field.conditions, wizardFields, field.conditionsOperator)
      : true
    // let fieldIsEnabled = !field.disabled;
    let fieldIsActive = field.active
    if (fieldIsActive && hasBusinessRules && conditionsSatsified) {
      let newPayload = {
        value,
        ...extraPayload,
      }
      if (
        secondaryValidation &&
        !field.requireDoubleValidation &&
        field.parent.type !== 'REPEATER' &&
        callback
      ) {
        console.warn(
          `double validation: disabled field business rules not run. fieldId: ${fieldId}`,
        )
        // do not loop through rules of fields that do not require double validation when doing a secondary review.
        newPayload.isValid = null
        // delete newPayload.value
        // delete newPayload.setValue
        callback(newPayload)
      } else {
        loopThroughRules(
          { businessRules: field.businessRules, value, field, event, options },
          (validation) => {
            let hasBusinessRules = validation.valids.length !== 0
            const validationSetValue = (validation.payload || {}).setValue
            // let fieldIsEnabled = !field.disabled;
            if (hasBusinessRules) {
              newPayload.isValid = validation.valids.every((valid) => {
                return valid === true
              })
              newPayload.errorMessage = validation.errors
              if (setValue === false || validationSetValue === false) {
                delete newPayload.value
                delete newPayload.setValue
              }
              newPayload = { ...newPayload, ...validation.payload }
            } else {
              newPayload.isValid = null
              newPayload.errorMessage = validation.errors
              if (setValue === false || validationSetValue === false) {
                delete newPayload.value
                delete newPayload.setValue
              }
              newPayload = { ...newPayload, ...validation.payload }
            }
            dispatch(setFieldAttributes({ fieldId, payload: newPayload }))
            if (callback) {
              callback(newPayload)
            }
          },
        )
      }
    } else if (setValue) {
      dispatch(
        setFieldAttributes({
          fieldId,
          payload: { value, ...extraPayload, isValid: null, errorMessage: [] },
        }),
      )
      if (callback) {
        callback({ value, ...extraPayload, isValid: null, errorMessage: [] })
      }
    } else {
      dispatch(
        setFieldAttributes({
          fieldId,
          payload: { isValid: null, errorMessage: [] },
        }),
      )
      if (callback) {
        callback({ isValid: true, errorMessage: [] })
      }
    }
  }

export const toggleValidating = (validating) => ({
  type: 'TOGGLE_VALIDATING',
  validating,
})

export const setActiveStep = (activeStep) => (dispatch, getState) => {
  if (getState().auth.loggedIn) {
    dispatch(extendUserSession())
  }

  dispatch({
    type: 'SET_ACTIVE_WIZARD_STEP',
    activeStep,
  })
}

export const setTargetStep = (targetStep) => ({
  type: 'SET_TARGET_STEP',
  targetStep,
})

export const validateAndSetActiveStep = (activeStep) => (dispatch, getState) => {
  dispatch(toggleValidating(true))
  const currentStep = getState().wizard.activeStep
  validateStep(currentStep, (stepIsValid) => {
    // if(stepIsValid || activeStep < currentStep){
    if (getState().wizard.infoMessages.length > 0) {
      dispatch(setTargetStep(activeStep))
    } else {
      dispatch(setActiveStep(activeStep))
    }
    // }
  })
}

export const buildFieldIdList = (fieldIds) => {
  let fieldList = [...fieldIds]
  const fields = store.getState().wizard.wizard.fields
  const loopThroughFieldIds = (fieldIds) => {
    fieldIds.forEach((fieldId) => {
      let field = fields[fieldId]
      let selectedIndex = field.selectedIndex
      if (field.hasOptionFields) {
        selectedIndex = field.options.findIndex(
          (option) => option.value.toString() === field.value.toString(),
        )
        if ((selectedIndex || selectedIndex === 0) && selectedIndex !== -1) {
          let subFieldIds = field.options[selectedIndex] ? field.options[selectedIndex].fields : []
          fieldList = [...fieldList, ...buildFieldIdList(subFieldIds)]
        }
      }
      if (field.hasCheckFields) {
        let fieldGroup = field.value ? 'checkedFields' : 'uncheckedFields'
        let subFieldIds = field[fieldGroup]
        fieldList = [...fieldList, ...buildFieldIdList(subFieldIds)]
      }
    })
  }
  loopThroughFieldIds(fieldIds)
  fieldList = fieldList.filter((fieldId) => {
    const field = fields[fieldId]
    const isDependentSubfield = field.parent.type === 'OPTION' || field.parent.type === 'CHECKBOX'
    const conditionsResolved = field.conditions
      ? resolveFieldConditions(field.conditions, fields, field.conditionsOperator)
      : true

    if (isDependentSubfield) {
      const parentField = fields[field.parent.id]
      const parentConditionsResolved = parentField.conditions
        ? resolveFieldConditions(parentField.conditions, fields, parentField.conditionsOperator)
        : true
      return conditionsResolved && parentConditionsResolved
    } else {
      return conditionsResolved
    }
  })
  return fieldList
}

export const buildFullWizardFieldIdList = () => {
  let stepIds = store.getState().wizard.wizard.stepOrder
  let fields = []

  stepIds.forEach((stepId, id) => {
    let sectionIds = store.getState().wizard.wizard.steps[stepId].sectionOrder
    let sections = store.getState().wizard.wizard.sections

    sectionIds.forEach((sectionId) => {
      let section = sections[sectionId]
      fields = [...fields, ...section.fieldOrder]
    })
  })
  fields = buildFieldIdList(fields)
  return fields
}

const fireGlobalBusinessRules = () => {
  const businessRules = (store.getState().wizard.wizard.globalVariables || {}).businessRule || {
    order: [],
    variables: {},
  }
  return businessRules.order.reduce(
    (obj, ruleId) => {
      const rule = businessRules.variables[ruleId]
      const result = resolveFieldEngine.resolveRootNode(rule.nodes)
      if (!result.isValid) {
        obj.isValid = false
        if (result.errorMessage) {
          obj.errorMessages.push(result.errorMessage)
        }
      }
      return obj
    },
    { isValid: true, errorMessages: [] },
  )
}

export const validateAllSteps = (callback) => {
  let stepIds = store.getState().wizard.wizard.stepOrder
  let stepPromises = []
  stepIds.forEach((stepId, i) => {
    stepPromises[i] = new Promise((resolve, reject) => {
      validateStep(i, (stepIsValid) => {
        resolve(stepIsValid)
      })
    })
  })
  Promise.all(stepPromises).then((values) => {
    const globalBusinessRuleResults = fireGlobalBusinessRules()
    const isValid =
      values.every((valid) => {
        return valid === true
      }) && globalBusinessRuleResults.isValid
    callback(isValid, globalBusinessRuleResults.errorMessages)
  })
}

const validateStep = (stepId, callback) => {
  let wizard = store.getState().wizard.wizard
  let step = wizard.steps[wizard.stepOrder[stepId]]
  let sections = wizard.sections
  let stepFields = []

  step.sectionOrder.forEach((sectionId) => {
    let fieldIds = sections[sectionId].fieldOrder
    stepFields = [...stepFields, ...buildFieldIdList(fieldIds)]
  })
  validateFieldArray(stepFields, (isValid) => {
    store.dispatch(setStepAttributes({ stepId: wizard.stepOrder[stepId], payload: { isValid } }))
    store.dispatch(toggleValidating(false))
    callback(isValid)
  })
}

export const validateFieldArray = (fieldArray, callback, options) => {
  let fieldPromises = []
  let fields = store.getState().wizard.wizard.fields
  // let wizard = store.getState().wizard.wizard
  for (let i = 0; i < fieldArray.length; i++) {
    fieldPromises[i] = new Promise((resolve, reject) => {
      store.dispatch(
        validateAndSetFieldAttributes(
          {
            fieldId: fields[fieldArray[i]].fieldId,
            event: 'onAdvance',
            setValue: false,
            value: fields[fieldArray[i]].value,
            options,
          },
          (valid) => {
            resolve(valid.isValid)
          },
        ),
      )
    })
  }
  Promise.all(fieldPromises).then((values) => {
    const isValid = values.every((valid) => {
      return valid === true || valid === null
    })
    callback(isValid)
  })
}

export const getGroupItems = (optionsSrc, subOptionsSrc, includeAllOption = false) =>
  new Promise((resolve, reject) => {
    api.GET_GROUP_ITEMS(
      optionsSrc + (subOptionsSrc !== '' && subOptionsSrc !== undefined ? '/' + subOptionsSrc : ''),
      includeAllOption,
      (res) => {
        const isArray = Array.isArray(res)
        let options = isArray
          ? res.map((option) => ({ ...option, value: option.value.toString() }))
          : res.options.map((option) => ({ ...option, value: option.value.toString() }))
        if (includeAllOption && options[0].label.toLowerCase() !== 'all') {
          const allOption = options.find((opt) => opt.label.toLowerCase() === 'all')
          if (!allOption)
            store.dispatch(
              setErrorToast(
                `Group Items ${optionsSrc} do not have an 'All' option and wizard may not save`,
              ),
            )
          options = options.filter((opt) => opt.label.toLowerCase() !== 'all')
          // This default All option may break if tables don't have their own All option inserted.
          options.unshift(
            allOption
              ? allOption
              : {
                  label: 'All',
                  value: '-1',
                },
          )
        }
        if (optionsSrc === 'States') {
          options = options.filter((opt) => opt.label.toLowerCase() !== 'not applicable')
        }
        resolve(
          isArray
            ? options
            : {
                options: options,
                optionGroups: res.optionGroups,
              },
        )
      },
    )
  })

export const getGroupItemsAndSetFieldOptions =
  (fieldId, providedField, callback) => (dispatch, getState) => {
    const fields = getState().wizard.wizard.fields
    const field = providedField || fields[fieldId]

    const optionsSrcOverride = field.optionsSrcOverride
      ? fields[field.optionsSrcOverride].value
      : field.optionsSrc

    const subOptionsSrcOverride = field.subOptionsSrcOverride
      ? fields[field.subOptionsSrcOverride].value
      : field.subOptionsSrc

    if (field.optionsSrcOverride) {
      dispatch(
        setFieldAttributes({
          fieldId,
          payload: { lastSeenOptionsSrcOverride: optionsSrcOverride },
        }),
      )
    }

    if (field.subOptionsSrcOverride) {
      dispatch(
        setFieldAttributes({
          fieldId,
          payload: { lastSeenSubOptionsSrcOverride: subOptionsSrcOverride },
        }),
      )
    }

    getGroupItems(optionsSrcOverride, subOptionsSrcOverride, field.includeAllOption).then((res) => {
      if (Array.isArray(res)) {
        if (callback) {
          callback(res)
        } else {
          dispatch(setFieldAttributes({ fieldId, payload: { options: res, optionsFetched: true } }))
        }
      } else {
        if (callback) {
          callback(res.options, res.optionGroups)
        } else {
          dispatch(
            setFieldAttributes({
              fieldId,
              payload: {
                options: res.options,
                optionGroups: res.optionGroups,
                optionsFetched: true,
              },
            }),
          )
        }
      }
    })
  }

export const saveRecord =
  (payload, category, mode, isCorrection, callback, errorCallback) => (dispatch, getState) => {
    dispatch(toggleValidating(true))

    if (payload.METADATA.INTERNAL_REVIEW_REQUIRED_YN instanceof Function) {
      payload.METADATA.INTERNAL_REVIEW_REQUIRED_YN = dispatch(
        payload.METADATA.INTERNAL_REVIEW_REQUIRED_YN,
      )
    }

    api.SAVE_RECORD(
      payload,
      category,
      mode,
      isCorrection,
      (response) => {
        dispatch(setWizardAttributes({ amendmentNum: response }))
        dispatch(toggleValidating(false))
        callback(response)
      },
      (e) => {
        dispatch(toggleValidating(false))
        errorCallback(e)
      },
    )
  }

export const saveRecordWithSignatures =
  (payload, category, mode, isCorrection, callback, errorCallback) => (dispatch, getState) => {
    dispatch(toggleValidating(true))
    api.SAVE_RECORD_WITH_SIGNATURES(
      payload,
      category,
      isCorrection,
      (response) => {
        dispatch(setWizardAttributes({ amendmentNum: response }))
        dispatch(toggleValidating(false))
        callback(response)
      },
      (e) => {
        dispatch(toggleValidating(false))
        errorCallback(e)
      },
    )
  }

export const toggleHelpModal = (helpShown) => ({
  type: 'TOGGLE_HELP_MODAL',
  helpShown,
})

export const setDraftId = (draftId) => ({
  type: 'SET_DRAFT_ID',
  draftId,
})

export const toggleSavingDraft = (savingDraft) => ({
  type: 'TOGGLE_SAVING_DRAFT',
  savingDraft,
})

export const saveDraft = (draft, callback) => (dispatch) => {
  dispatch(toggleSavingDraft(true))
  api.SAVE_DRAFT(draft, (draftId) => {
    dispatch(setDraftId(draftId))
    dispatch(toggleSavingDraft(false))
    if (callback) callback()
  })
}

export const resetWizardState = () => ({
  type: 'RESET_WIZARD_STATE',
})

export const setUploading = (uploading) => ({
  type: 'SET_UPLOADING',
  uploading,
})

export const getRecordsByUser = (searchType, callback) => (dispatch) => {
  api.GET_RECORDS_BY_USER(searchType, { metadata: store.getState().wizard.wizard.meta }, (data) => {
    if (callback) {
      callback(data)
    }
  })
}

export const clearInfoMessages = () => ({
  type: 'CLEAR_INFO_MESSAGES',
})

export const addInfoMessage = (message) => ({
  type: 'ADD_INFO_MESSAGE',
  message,
})

export const calculateInternalReview = () => (dispatch, getState) => {
  const state = getState().wizard
  const wizard = state.wizard
  if (wizard.internalReviewRequired === true) {
    return true
  } else {
    return (
      getFieldsWithChangedValues().filter(
        (field) => field.internalReviewRequiredIfChanged || field.requireInternalReview,
      ).length > 0 || wizard.internalReviewRequiredSet.size > 0
    )
  }
}

export const addRequirePrintMailMethod = (fieldId) => ({
  type: 'ADD_REQUIRE_PRINT_MAIL_METHOD',
  fieldId,
})

export const removeRequirePrintMailMethod = (fieldId) => ({
  type: 'REMOVE_REQUIRE_PRINT_MAIL_METHOD',
  fieldId,
})

export const spreadWizardAttributes = (payload) => ({
  type: 'SPREAD_WIZARD_ATTRIBUTES',
  payload,
})

export const reconstructWizardWithValues = (fieldValues) => (dispatch, getState) => {
  const wizard = getState().wizard.wizard
  const parsedWizard = CreateInstancesFromJson(
    {
      stepOrder: wizard.stepOrder,
      steps: wizard.steps,
      sections: wizard.sections,
      fields: wizard.fields,
    },
    fieldValues,
  )
  prefetchRequiredData(parsedWizard.fields)
    .then(preFetchMergeCodes)
    .then((fields) => {
      dispatch(spreadWizardAttributes({ ...parsedWizard, fields }))
    })
}

export const setWizardSubmitted = (submitted) => ({
  type: 'SET_SUBMITTED',
  submitted,
})

export const setWizardSubmitting = (submitting) => ({
  type: 'SET_SUBMITTING',
  submitting,
})

export const buildFieldNameMap = (fieldIds, fields) =>
  fieldIds.reduce((obj, fieldId) => {
    const field = fields[fieldId]
    const fieldName = field.name
    obj[fieldName] = (obj[fieldName] || 0) + 1
    return obj
  }, {})

export const createMetaCollection = (FIELD_META, wizard, filteredFieldIds) => {
  const fieldNameMap = buildFieldNameMap(filteredFieldIds, wizard.wizard.fields)

  return filteredFieldIds.reduce((meta, key) => {
    const field = wizard.wizard.fields[key]
    const fieldNameIsNotUnique = fieldNameMap[field.name] > 1
    const needsUniqueFieldName = field.disabled && !field.hideFieldOnForm && fieldNameIsNotUnique
    const metaKey = needsUniqueFieldName ? field.fieldId : field.name
    return {
      ...meta,
      [metaKey]: {
        ...(FIELD_META[metaKey] || {}),
        hasChanged: (wizard.diff.original || {}).hasOwnProperty(field.name)
          ? WizardClasses[field.fieldClass].compareValues(
              wizard.diff.original[field.name],
              field.value,
            )
          : null,
        fieldType: field.type,
        fieldNameIsUnique: !fieldNameIsNotUnique,
        requireDoubleValidation: field.requireDoubleValidation || false,
      },
    }
  }, {})
}

// const getParentKey = (field, wizard) => {
//   const parent = wizard.wizard.fields[field.parent.id]
//   const needsUniqueFieldName = parent.disabled && !parent.hideFieldOnForm
//   return needsUniqueFieldName ? parent.fieldId : parent.name
// }

export const createEncryptionCollection = (wizard) =>
  Object.keys(wizard.wizard.fields).reduce((arr, key) => {
    return arr
  }, [])

export const setWizardLoadFailed = (wizardLoadFailed) => ({
  type: 'SET_WIZARD_LOAD_FAILED',
  wizardLoadFailed,
})

export const setWizardSignatureValues = (signatureValues) => ({
  type: 'SET_WIZARD_SIGNATURE_VALUES',
  signatureValues,
})

export const resetWizardSignatureValues = (signatureValues) => ({
  type: 'RESET_WIZARD_SIGNATURE_VALUES',
  signatureValues,
})

export const setWizardSignatureErrors = (signatureErrors) => ({
  type: 'SET_WIZARD_SIGNATURE_ERRORS',
  signatureErrors,
})

export const setGenericSuccess = (message) => ({
  type: 'SET_GENERIC_SUCCESS',
  message,
})

export const createFieldOverridesCollection = (wizard) =>
  Object.values(wizard.wizard.fields).reduce((obj, field) => {
    obj[field.fieldId] = Object.entries(resolveFieldLogic(field)).reduce((obj, [key, value]) => {
      if (!deepEqual(field[key], value)) {
        obj[key] = value
      }
      return obj
    }, {})
    return obj
  }, {})

export const resolveWizardAttributesLogic = () => (dispatch, getState) => {
  const wizard = store.getState().wizard.wizard
  const wizardOverrides =
    Object.keys(wizard.wizardAttributesLogic.nodes).length > 0
      ? resolveFieldEngine.resolveRootNode(wizard.wizardAttributesLogic.nodes, {
          onlyResolveConnected: true,
        })
      : {}
  return { ...wizard, ...wizardOverrides }
}
